import type { Locale } from "@/lib/i18n/getLocale";
import type { CategorySlug, ColorTag } from "@/lib/catalog/types";

// Backward-compat alias for code that previously imported ColorSlug.
export type ColorSlug = ColorTag;
export type { CategorySlug, ColorTag };

export type LengthOption = 40 | 50 | 60 | 70 | 80 | 90;

type Dict = {
  catalogTitle: string;
  categoryAll: string;
  category: Record<CategorySlug, string>;
  // #424: the color-label map. Keyed by ColorTag PLUS the selectable colors that
  // are not base ColorTags (apricot, picotee) so no chosen color renders
  // untranslated on an order line. Filter chips iterate a fixed COLORS list, not
  // these keys, so extra entries add labels only - no new filter chips.
  color: Record<ColorTag | "apricot" | "picotee" | "burgundy", string>;
  filterCategory: string;
  filterLength: string;
  filterColor: string;
  fromPerBunch: (price: number) => string;
  signInToSeePricing: string;
  photoComingSoon: string;
  backToCatalog: string;
  orderingControlsLater: string;
  addedToCart: string;
  filterButtonLabel: string;
  noResults: string;
  detailHeadSize: string;
  detailPetalCount: string;
  detailStemsPerBunch: string;
  detailByBunchByBoxPlaceholder: string;
  lengthSuffixCm: string;
  length80Label: string;
  harvestedLabel: string;
  arrivesLabel: string;
  deliveryNextDelivery: string;
  deliveryCountdownPrefix: string;
  deliveryCutoffPassedShipping: string;
  deliveryBannerDismiss: string;
  catalogUnavailable: string;
  aboutTheFarm: string;
  aboutTheFarmBlurb: (altitudeMeters: number, region: string, country: string) => string;
  // Step 8.5 Half B commit 17: internal-linking section headings.
  relatedColorFamily: string;
  relatedAlsoBrowsed: string;
  specRowPetalsUnit: string;
  specRowStemsUnit: string;
  // Step 6.7.1 i18n pass.
  fmBannerLine: string;
  buyBy: string;
  boxSizeLabel: string;
  quantityLabel: string;
  colorLabel: string;
  lengthLabel: string;
  gradeLabel: string;
  unitBox: string;
  unitBunch: string;
  unitBoxes: string;
  unitBunches: string;
  cancel: string;
  add: string;
  added: string;
  viewCart: string;
  mix: string;
  addedNBunchesOf: (qty: number, name: string, color: string) => string;
  addedNBoxesOf: (qty: number, sizeCode: string, name: string, color: string) => string;
  mixConflictOpenedSecond: (sizeCode: string, name: string) => string;
};

const en: Dict = {
  catalogTitle: "Catalog",
  categoryAll: "All",
  category: {
    rose: "Roses",
    "garden-spray-rose": "Garden & Spray Roses",
    hydrangea: "Hydrangeas",
    specialty: "Specialty",
    "filler-green": "Fillers & Greens",
  },
  color: {
    red: "Red",
    white: "White",
    cream: "Cream",
    pink: "Pink",
    "hot-pink": "Hot Pink",
    peach: "Peach",
    blue: "Blue",
    green: "Green",
    purple: "Purple",
    orange: "Orange",
    yellow: "Yellow",
    bicolor: "Bicolor",
    lavender: "Lavender",
    brown: "Brown",
    sand: "Sand",
    apricot: "Apricot",
    picotee: "Picotee",
    burgundy: "Burgundy",
  },
  filterCategory: "Category",
  filterLength: "Length",
  filterColor: "Color",
  fromPerBunch: (price: number) => `From $${price.toFixed(2)} / bunch`,
  signInToSeePricing: "Sign in to see pricing",
  photoComingSoon: "Photo coming soon",
  backToCatalog: "Back to catalog",
  orderingControlsLater: "Ordering controls coming in the next step.",
  addedToCart: "Added to cart",
  filterButtonLabel: "Filter",
  noResults: "No varieties match your filters.",
  detailHeadSize: "Head size",
  detailPetalCount: "Petal count",
  detailStemsPerBunch: "Stems per bunch",
  detailByBunchByBoxPlaceholder: "By Bunch / By Box",
  lengthSuffixCm: "cm",
  length80Label: "80-90",
  harvestedLabel: "Harvested",
  arrivesLabel: "Arrives",
  deliveryNextDelivery: "Next delivery",
  deliveryCountdownPrefix: "Cutoff in",
  deliveryCutoffPassedShipping: "Cutoff passed - next delivery",
  deliveryBannerDismiss: "Dismiss delivery banner",
  catalogUnavailable: "Catalog temporarily unavailable. Try again in a moment.",
  aboutTheFarm: "About the farm",
  relatedColorFamily: "Same color family",
  relatedAlsoBrowsed: "Customers also browsed",
  aboutTheFarmBlurb: (altitudeMeters: number, region: string, country: string) =>
    `Grown at ${altitudeMeters.toLocaleString("en-US")}m in ${region}, ${country}.`,
  specRowPetalsUnit: "petals",
  specRowStemsUnit: "stems/bunch",
  fmBannerLine: "OUR BRANDED ROSES · GROWN AT 3,200M",
  buyBy: "Buy by",
  boxSizeLabel: "Box size",
  quantityLabel: "Quantity",
  colorLabel: "Color",
  lengthLabel: "Length",
  gradeLabel: "Grade",
  unitBox: "Box",
  unitBunch: "Bunch",
  unitBoxes: "boxes",
  unitBunches: "bunches",
  cancel: "Cancel",
  add: "Add",
  added: "Added",
  viewCart: "View cart",
  mix: "Mix",
  addedNBunchesOf: (qty, name, color) =>
    `Added ${qty} ${qty === 1 ? "bunch" : "bunches"} of ${name}${color ? " " + color : ""} to box`,
  addedNBoxesOf: (qty, sizeCode, name, color) =>
    `Added ${qty} ${sizeCode} of ${name}${color ? " " + color : ""} to box`,
  mixConflictOpenedSecond: (sizeCode, name) =>
    `Mix conflict - opened a 2nd ${sizeCode} box for ${name}`,
};

const es: Dict = {
  catalogTitle: "Catálogo",
  categoryAll: "Todos",
  category: {
    rose: "Rosas",
    "garden-spray-rose": "Rosas de jardín y spray",
    hydrangea: "Hortensias",
    specialty: "Especiales",
    "filler-green": "Rellenos y Verdes",
  },
  color: {
    red: "Rojo",
    white: "Blanco",
    cream: "Crema",
    pink: "Rosa",
    "hot-pink": "Rosa Fuerte",
    peach: "Durazno",
    blue: "Azul",
    green: "Verde",
    purple: "Morado",
    orange: "Naranja",
    yellow: "Amarillo",
    bicolor: "Bicolor",
    lavender: "Lavanda",
    brown: "Marrón",
    sand: "Arena",
    // #424: apricot -> "Durazno" (Alexis-confirmed; the LatAm florist term for
    // this peach tone - shares the ES word with "peach", intentionally).
    apricot: "Durazno",
    // picotee IS a live selectable color (Select Carnation colorOptions); trade
    // term, same in Spanish. PLACEHOLDER - flagged for Alexis to confirm.
    picotee: "Picotee",
    // burgundy is selectable too (Select Carnation); "Borgoña" is the ES word
    // already used in variety-descriptor.ts COLOR_ES - reused for congruence.
    burgundy: "Borgoña",
  },
  filterCategory: "Categoría",
  filterLength: "Largo",
  filterColor: "Color",
  fromPerBunch: (price: number) => `Desde $${price.toFixed(2)} / bonche`,
  signInToSeePricing: "Inicia sesión para ver precios",
  photoComingSoon: "Foto próximamente",
  backToCatalog: "Volver al catálogo",
  orderingControlsLater: "Los controles de pedido llegan en el próximo paso.",
  addedToCart: "Agregado al carrito",
  filterButtonLabel: "Filtrar",
  noResults: "Ninguna variedad coincide con tus filtros.",
  detailHeadSize: "Tamaño de cabeza",
  detailPetalCount: "Número de pétalos",
  detailStemsPerBunch: "Tallos por bonche",
  detailByBunchByBoxPlaceholder: "Por Bonche / Por Caja",
  lengthSuffixCm: "cm",
  length80Label: "80-90",
  harvestedLabel: "Cosechada",
  arrivesLabel: "Llega",
  deliveryNextDelivery: "Próxima entrega",
  deliveryCountdownPrefix: "Cierre en",
  deliveryCutoffPassedShipping: "Cierre pasado - próxima entrega",
  deliveryBannerDismiss: "Descartar aviso de entrega",
  catalogUnavailable: "Catálogo temporalmente no disponible. Intenta de nuevo en un momento.",
  aboutTheFarm: "Sobre la finca",
  relatedColorFamily: "Misma familia de color",
  relatedAlsoBrowsed: "Los clientes también vieron",
  aboutTheFarmBlurb: (altitudeMeters: number, region: string, country: string) =>
    `Cultivada a ${altitudeMeters.toLocaleString("es-ES")} m en ${region}, ${country}.`,
  specRowPetalsUnit: "pétalos",
  specRowStemsUnit: "tallos/bonche",
  fmBannerLine: "NUESTRAS ROSAS · CULTIVADAS A 3.200 M",
  buyBy: "Comprar por",
  boxSizeLabel: "Tamaño de caja",
  quantityLabel: "Cantidad",
  colorLabel: "Color",
  lengthLabel: "Largo",
  gradeLabel: "Grado",
  unitBox: "Caja",
  unitBunch: "Bonche",
  unitBoxes: "cajas",
  unitBunches: "bonches",
  cancel: "Cancelar",
  add: "Agregar",
  added: "Agregado",
  viewCart: "Ver carrito",
  mix: "Mix",
  addedNBunchesOf: (qty, name, color) =>
    `Se agregaron ${qty} ${qty === 1 ? "bonche" : "bonches"} de ${name}${color ? " " + color : ""} a la caja`,
  addedNBoxesOf: (qty, sizeCode, name, color) =>
    `Se agregaron ${qty} ${sizeCode} de ${name}${color ? " " + color : ""} a la caja`,
  mixConflictOpenedSecond: (sizeCode, name) =>
    `Conflicto de mezcla - se abrió una 2da caja ${sizeCode} para ${name}`,
};

export const CATALOG_STRINGS: Record<Locale, Dict> = { en, es };

export function getCatalogStrings(locale: Locale): Dict {
  return CATALOG_STRINGS[locale];
}

export type CatalogStrings = Dict;

export type PlainCatalogStrings = Omit<
  Dict,
  | "fromPerBunch"
  | "aboutTheFarmBlurb"
  | "addedNBunchesOf"
  | "addedNBoxesOf"
  | "mixConflictOpenedSecond"
>;

export function getPlainCatalogStrings(locale: Locale): PlainCatalogStrings {
  const full = CATALOG_STRINGS[locale];
  const {
    fromPerBunch: _unusedFromPerBunch,
    aboutTheFarmBlurb: _unusedAboutBlurb,
    addedNBunchesOf: _unusedAddedBunches,
    addedNBoxesOf: _unusedAddedBoxes,
    mixConflictOpenedSecond: _unusedMixConflict,
    ...plain
  } = full;
  void _unusedFromPerBunch;
  void _unusedAboutBlurb;
  void _unusedAddedBunches;
  void _unusedAddedBoxes;
  void _unusedMixConflict;
  return plain;
}
